import type { ReactNode } from 'react';
import type { SpeechKey } from './skill-creator-api';

import {
  useRef,
  useState,
  useEffect,
  useContext,
  useCallback,
  createElement,
  createContext,
  useSyncExternalStore,
} from 'react';

export type FetchSpeechKey = () => Promise<SpeechKey>;

const SpeechKeyContext = createContext<FetchSpeechKey | null>(null);

/** Lets every message box below it turn speech into text with Soniox, the speech to text of Ari's voice. */
export function SpeechToTextProvider({ fetchKey, children }: { fetchKey: FetchSpeechKey; children: ReactNode }) {
  return createElement(SpeechKeyContext.Provider, { value: fetchKey }, children);
}

// Audio is handed over in blocks of this many samples, about a tenth of a second.
const BLOCK = 4096;

/** A 0 to 1 loudness for the level bars. Speech sits well below full scale, so it is scaled up. */
export function loudness(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
  const rms = Math.sqrt(sum / Math.max(samples.length, 1));
  return Math.min(1, rms * 6);
}

/** 16-bit PCM, the format Soniox reads. */
function toPcm(samples: Float32Array): ArrayBuffer {
  const pcm = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return pcm.buffer;
}

// Soniox marks some events with tokens such as <end> or <fin>. They are not words.
const CONTROL_TOKEN = /^<[a-z]+>$/;
// WebSocket ready states, as numbers so a closed page never needs the WebSocket class.
const CONNECTING = 0;
const OPEN = 1;
// A microphone left on with nothing new said stops by itself, so it does not stream for nothing.
export const IDLE_STOP_MS = 20000;
// After the end of the audio, Soniox sends the last words and then says it has finished.
const FINISH_WAIT_MS = 4000;

export interface LevelSignal {
  get: () => number;
  subscribe: (onChange: () => void) => () => void;
}

function levelSignal(): LevelSignal & { set: (value: number) => void } {
  let current = 0;
  const listeners = new Set<() => void>();
  return {
    get: () => current,
    subscribe: (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    set: (value) => {
      // Small changes are not worth a redraw.
      if (Math.abs(value - current) < 0.02) return;
      current = value;
      listeners.forEach((onChange) => onChange());
    },
  };
}

/** The microphone level, for the one component that draws it. */
export function useLevel(level: LevelSignal): number {
  return useSyncExternalStore(level.subscribe, level.get, level.get);
}

export type DictationProblem = 'blocked' | 'failed' | null;

export interface Dictation {
  /** A key fetcher is set up and the browser can record. */
  supported: boolean;
  listening: boolean;
  /** How loud the microphone is right now, from 0 to 1. Read it with useLevel, so only the bars redraw. */
  level: LevelSignal;
  problem: DictationProblem;
  start: () => void;
  /** Ends the recording. Resolves once the last words are in the box. */
  stop: () => Promise<void>;
}

interface Session {
  idle: ReturnType<typeof setTimeout> | null;
  stream: MediaStream | null;
  context: AudioContext | null;
  socket: WebSocket | null;
  finished: Promise<void>;
  finish: () => void;
}

function canRecord(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.WebSocket !== 'undefined' &&
    typeof window.AudioContext !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

/**
 * Speech to text into a text box. What the author says is added after what is already in
 * the box, and shows as they speak.
 */
export function useDictation(value: string, onChange: (text: string) => void, lang?: string): Dictation {
  const fetchKey = useContext(SpeechKeyContext);
  const [listening, setListening] = useState(false);
  const [problem, setProblem] = useState<DictationProblem>(null);
  const level = useRef(levelSignal()).current;
  const session = useRef<Session | null>(null);
  const latestChange = useRef(onChange);
  latestChange.current = onChange;
  const latestValue = useRef(value);
  latestValue.current = value;

  const release = useCallback((s: Session) => {
    if (s.idle) clearTimeout(s.idle);
    s.idle = null;
    s.stream?.getTracks().forEach((track) => track.stop());
    s.stream = null;
    void s.context?.close().catch(() => undefined);
    s.context = null;
  }, []);

  const end = useCallback(
    (s: Session) => {
      release(s);
      if (s.socket && s.socket.readyState <= OPEN) s.socket.close();
      s.socket = null;
      s.finish();
      // An older session that ends late must not switch off the one now running.
      if (session.current !== s) return;
      session.current = null;
      setListening(false);
      level.set(0);
    },
    [level, release]
  );

  const stop = useCallback(async () => {
    const s = session.current;
    if (!s) return;
    release(s);
    if (s.socket?.readyState === OPEN) {
      // An empty message ends the audio. Soniox then sends the last words and finishes.
      s.socket.send('');
      await Promise.race([s.finished, new Promise((r) => setTimeout(r, FINISH_WAIT_MS))]);
    }
    end(s);
  }, [end, release]);

  const stopRef = useRef(stop);
  stopRef.current = stop;

  const start = useCallback(() => {
    if (!fetchKey || session.current) return;
    let finish!: () => void;
    const finished = new Promise<void>((resolve) => {
      finish = resolve;
    });
    const s: Session = { idle: null, stream: null, context: null, socket: null, finished, finish };
    session.current = s;
    setProblem(null);
    setListening(true);
    const typed = latestValue.current;
    const before = typed.trim() ? `${typed.trimEnd()} ` : '';
    let finalText = '';
    let lastSaid = '';
    const fail = (kind: DictationProblem) => {
      if (session.current === s) setProblem(kind);
      end(s);
    };
    const stillThere = () => {
      if (s.idle) clearTimeout(s.idle);
      s.idle = setTimeout(() => {
        if (session.current === s) void stopRef.current();
      }, IDLE_STOP_MS);
    };

    // The audio context starts inside the tap itself. Browsers keep audio started later,
    // after the key and the microphone prompt, suspended.
    try {
      s.context = new AudioContext();
      void s.context.resume().catch(() => undefined);
    } catch {
      fail('failed');
      return;
    }

    void (async () => {
      let key: SpeechKey;
      try {
        key = await fetchKey();
      } catch {
        fail('failed');
        return;
      }
      try {
        s.stream = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
        });
      } catch (error) {
        fail(error instanceof DOMException && error.name === 'NotAllowedError' ? 'blocked' : 'failed');
        return;
      }
      if (session.current !== s) {
        release(s);
        return;
      }
      const context = s.context;
      if (!context) return;
      try {
        // The script processor is old but every browser has it. An audio worklet would need a
        // module load, which Chrome was seen to never finish.
        const tap = context.createScriptProcessor(BLOCK, 1, 1);
        // The tap only runs when it reaches the end of the graph, so it goes there silently.
        const mute = context.createGain();
        mute.gain.value = 0;
        context.createMediaStreamSource(s.stream).connect(tap);
        tap.connect(mute).connect(context.destination);

        const socket = new WebSocket(key.websocket_url);
        s.socket = socket;
        const waiting: ArrayBuffer[] = [];
        tap.onaudioprocess = (event: AudioProcessingEvent) => {
          const samples = event.inputBuffer.getChannelData(0);
          level.set(loudness(samples));
          const chunk = toPcm(samples);
          if (socket.readyState === OPEN) socket.send(chunk);
          else if (socket.readyState === CONNECTING) waiting.push(chunk);
        };
        socket.onopen = () => {
          socket.send(
            JSON.stringify({
              api_key: key.api_key,
              model: key.model,
              audio_format: 'pcm_s16le',
              sample_rate: context.sampleRate,
              num_channels: 1,
              language_hints: [(lang || navigator.language || 'en').split('-')[0]],
            })
          );
          waiting.splice(0).forEach((chunk) => socket.send(chunk));
          stillThere();
        };
        socket.onmessage = (event: MessageEvent<string>) => {
          let message: {
            tokens?: { text: string; is_final: boolean }[];
            finished?: boolean;
            error_code?: number | string;
          };
          try {
            message = JSON.parse(event.data);
          } catch {
            return;
          }
          if (message.error_code) {
            fail('failed');
            return;
          }
          let pending = '';
          for (const token of message.tokens ?? []) {
            if (CONTROL_TOKEN.test(token.text)) continue;
            if (token.is_final) finalText += token.text;
            else pending += token.text;
          }
          if (message.tokens?.length) {
            const said = (finalText + pending).trimStart();
            if (said !== lastSaid) stillThere();
            lastSaid = said;
            latestChange.current(before + said);
          }
          if (message.finished) s.finish();
        };
        socket.onerror = () => fail('failed');
        socket.onclose = () => {
          if (session.current === s) end(s);
        };
      } catch {
        fail('failed');
      }
    })();
  }, [end, fetchKey, lang, release, level]);

  // Leaving the page stops the microphone.
  useEffect(
    () => () => {
      if (session.current) end(session.current);
    },
    [end]
  );

  return { supported: fetchKey !== null && canRecord(), listening, level, problem, start, stop };
}
